import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { contentApi } from '../../services/contentApi';
import { GigListItem } from '../../types/content';
import { canWriteGigs, isOwner } from '../../utils/roles';
import { useAuth } from '../../context/AuthContext';
import Button from '../../components/UI/Button';
import { confirmAction } from '../../utils/confirmAction';

const MyGigsPage: React.FC = () => {
  const { user } = useAuth();
  const owner = isOwner(user);
  const [gigs, setGigs] = useState<GigListItem[]>([]);

  useEffect(() => {
    contentApi.myGigs().then((r) => setGigs(r.data.gigs || [])).catch(() => undefined);
  }, []);

  if (!canWriteGigs(user)) {
    return <div className="max-w-4xl mx-auto px-4 py-10 text-gray-600">You do not have permission to manage gigs.</div>;
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-10">
      <div className="flex justify-between items-center gap-3">
        <h1 className="text-3xl font-bold text-gray-900">Gigs</h1>
        <Link to="/admin/gigs/new"><Button>New gig</Button></Link>
      </div>
      {gigs.length === 0 && <p className="mt-6 text-gray-500">You have not created any gigs yet.</p>}
      <div className="mt-6 space-y-3">
        {gigs.map((gig) => (
          <div key={gig.id} className="rounded-xl border border-gray-200 p-4 hover:border-primary-200 bg-white flex justify-between gap-3">
            <Link to={`/admin/gigs/${gig.id}/edit`} className="flex-1">
              <div className="flex justify-between gap-3">
                <h2 className="font-semibold text-gray-900">{gig.title}</h2>
                <span className="text-sm text-gray-500">{gig.status}</span>
              </div>
              <p className="text-sm text-gray-600 mt-1">{gig.clientName} · {gig.teamType} · {gig.summary || gig.subtitle}</p>
            </Link>
            <div className="flex flex-col items-end gap-2 shrink-0">
              <Button
                variant="outline"
                onClick={async () => {
                  const ok = await confirmAction({
                    title: 'Archive this gig?',
                    description: `“${gig.title}” will be deactivated (soft delete).`,
                    confirmLabel: 'Archive',
                    cancelLabel: 'Keep it',
                    variant: 'danger',
                  });
                  if (!ok) return;
                  contentApi.archiveGig(gig.id).then(() => setGigs((current) => current.filter((item) => item.id !== gig.id)));
                }}
              >
                Archive
              </Button>
              {owner ? (
                <button
                  type="button"
                  className="text-sm text-error-700 font-medium"
                  onClick={async () => {
                    const ok = await confirmAction({
                      title: 'Delete gig permanently?',
                      description: `“${gig.title}” will be permanently removed. This cannot be undone.`,
                      confirmLabel: 'Delete permanently',
                      cancelLabel: 'Cancel',
                      variant: 'danger',
                    });
                    if (!ok) return;
                    contentApi.permanentDeleteGig(gig.id).then(() => setGigs((current) => current.filter((item) => item.id !== gig.id)));
                  }}
                >
                  Delete permanently
                </button>
              ) : null}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default MyGigsPage;
